// src/components/auth/ConnectButton.tsx
import React, { useMemo, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useAuth } from "@crossmint/client-sdk-react-ui";
import { useAppContext } from "context/AppContextProvider";
import { useTranslation } from "react-i18next";

interface Props {
  className?: string;
}

const ConnectButton: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const { login, logout, user } = useAuth();
  const { isWalletConnecting, isWalletConnected, isAuthenticated } =
    useAppContext();

  const isLoggedIn = useMemo(
    () => Boolean(isAuthenticated || user),
    [isAuthenticated, user]
  );

  const label = useMemo(() => {
    if (isLoggedIn) {
      return t("common.buttons.disconnect.label");
    }

    if (loading || isWalletConnecting) {
      return t("common.buttons.connect.loading");
    }

    return t("common.buttons.connect.label");
  }, [isLoggedIn, loading, isWalletConnecting, t]);

  const handleLogin = async () => {
    setLoading(true);
    try {
      await login();
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);
    try {
      await logout();
    } finally {
      setLoading(false);
    }
  };

  const onClick = () => {
    if (!isLoggedIn) {
      void handleLogin();
    } else {
      void handleLogout();
    }
  };

  if (isLoggedIn) {
    return null;
  }

  return (
    <Button
      className={`${className ?? ""} reverse`}
      onClick={onClick}
      disabled={loading || isWalletConnecting}
    >
      {loading ? <Spinner color="white" /> : label}
    </Button>
  );
};

export default ConnectButton;
